import React, {
	useCallback,
	useState,
	useEffect,
} from 'react';

import CustomIcon from '../Common/CustomIcon/CustomIcon';
import VultureTable from './VultureTable';

// Import styles
import style from './BuildingsTable.module.css';

import BuildingsModal from './BuildingsModal';

import {
	deleteBuildings,
} from 'Vulture/Buildings';

const BuildingsTable = (props) => {
	const {
		app,
		systemGroupUUID,
		array,
		columnsToHide,
		tableSettings,
		fd,
		closeModal,
		toggleSelectAllRows,
	} = props;

	const [itemsPerPage, setItemsPerPage,] = useState(50);
	const [ page, setPage, ] = useState(1);
	const [ pageNavigationBlocked, setPageNavigationBlocked, ] = useState(false);
	const [ filteredRowsCount, setFilteredRowsCount, ] = useState(array.length);
	const [globalSearchQuery, setGlobalSearchQuery,] = useState('');
	const [showColumnMenu, setShowColumnMenu,] = useState(false);
	const columnMenuRef = React.useRef(null);
	const columnMenuButtonRef = React.useRef(null);

	useEffect(() => {
		if (!showColumnMenu) return;
		const handleClickOutside = (event) => {
			if (
				columnMenuRef.current &&
				!columnMenuRef.current.contains(event.target) &&
				columnMenuButtonRef.current &&
				!columnMenuButtonRef.current.contains(event.target)
			) {
				setShowColumnMenu(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showColumnMenu]);
	
	const accountUUID = (app && app.state && app.state.accountUUID) ? app.state.accountUUID : '';
	const buildingsViewCacheKey = `buildings_table_view_${accountUUID || 'global'}`;
	const hiddenColsStorageKey = `${buildingsViewCacheKey}_hiddenColumns`;
	const colFlexStorageKey = `${buildingsViewCacheKey}_dataColumnFlexPx`;

	const [hiddenColumns, setHiddenColumns] = React.useState(() => {
		try {
			const saved = localStorage.getItem(hiddenColsStorageKey);
			const parsed = saved ? JSON.parse(saved) : null;
			return Array.isArray(parsed) ? parsed : (columnsToHide || []);
		} catch (e) {
			return columnsToHide || [];
		}
	});

	React.useEffect(() => {
		try {
			const saved = localStorage.getItem(hiddenColsStorageKey);
			const parsed = saved ? JSON.parse(saved) : null;
			setHiddenColumns(Array.isArray(parsed) ? parsed : (columnsToHide || []));
		} catch (e) {
			setHiddenColumns(columnsToHide || []);
		}
	}, [hiddenColsStorageKey, columnsToHide]);

	React.useEffect(() => {
		try {
			localStorage.setItem(hiddenColsStorageKey, JSON.stringify(hiddenColumns));
		} catch (e) {}
	}, [hiddenColumns, hiddenColsStorageKey]);

	useEffect(() => {
		if (filteredRowsCount >= itemsPerPage && pageNavigationBlocked === true) {
			setPageNavigationBlocked(false);
		} else if (filteredRowsCount < itemsPerPage && pageNavigationBlocked === false) {
			setPageNavigationBlocked(true);
		}
	}, [filteredRowsCount, pageNavigationBlocked, page, itemsPerPage,]);

	const onFilter = useCallback((_, preFilteredRows) => {
		setFilteredRowsCount(preFilteredRows.length);
	}, []);

	const previousPageAvailable = (page > 1) && !pageNavigationBlocked;
	const nextPageAvailable = (page * itemsPerPage < filteredRowsCount) && !pageNavigationBlocked;

	let disabledSquare = (
		<div className={style.disabledSquare}>
			<div className={style.checkboxIcon}>
				<CustomIcon icon="check"/>
			</div>
		</div>
	);

	let activeCheckbox = (
		<div className={style.activeCheckbox}>
			<div className={style.checkboxIcon}>
				<CustomIcon icon="check"/>
			</div>
		</div>
	);

	const data = React.useMemo(() => {
		if (!globalSearchQuery.trim()) return array;
		
		const q = globalSearchQuery.trim().replace(/\s+/g, ' ').toLowerCase();
		
		return array.filter(item => {
			let match = false;
			
			if (!hiddenColumns.includes('buildingName')) {
				const bName = (item.vals.buildingName || '').toLowerCase();
				if (bName.includes(q)) match = true;
			}
			
			if (!match && !hiddenColumns.includes('displayName')) {
				const dName = (item.vals.displayName || '').toLowerCase();
				if (dName.includes(q)) match = true;
			}
			
			if (!match && !hiddenColumns.includes('property')) {
				const groupKey = `system_group.${item.system_group}`;
				const pName = (tableSettings[groupKey] ? tableSettings[groupKey] : '').toLowerCase();
				if (pName.includes(q)) match = true;
			}
			
			return match;
		});
	}, [array, globalSearchQuery, tableSettings, hiddenColumns]);

	const allRowsSelected = data.length > 0 && data.every(item => item.isChecked);

	let headerSelectAll = (
		<button
			type="button"
			className={allRowsSelected ? style.activeCheckbox : style.disabledSquare}
			aria-label={allRowsSelected ? 'Deselect all buildings' : 'Select all buildings'}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				const buildingsView = props.buildingsView;
				
				const filteredM1Keys = new Set(data.map(b => b.m1Key));
				const buildingsDataArray = array.map((b) => {
					if (filteredM1Keys.has(b.m1Key)) {
						return { ...b, isChecked: !allRowsSelected };
					}
					return b;
				});

				if (buildingsView && typeof buildingsView.setState === 'function') {
					buildingsView.setState({ buildingsDataArray });
				}
			}}>
			<div className={style.checkboxIcon}>
				<CustomIcon icon="check" />
			</div>
		</button>
	);

	let buildingColumns = [
		{
			id:       'checkbox',
			Header:   headerSelectAll,
			accessor: (d) => {
				return d.isChecked ? activeCheckbox : disabledSquare;
			},
			disableFilters: true,
		},
		{
			id:       'buildingName',
			Header:   'Building Name',
			accessor: (d) => {
				return d.vals.buildingName;
			},
			disableFilters: true,
		},
		{
			id:       'displayName',
			Header:   'Display Name',
			accessor: (d) => {
				return d.vals.displayName;
			},
			disableFilters: true,
		},
	];


	buildingColumns.push({
		id:      'property',
		Header:  'Assigned to',
		accessor: (d) => {
			const groupKey = `system_group.${d.system_group}`;
			const groupName = tableSettings[groupKey];
			return (d.system_group && groupName) ? groupName : '';
		},
		Cell: ({ row }) => {
			const d = row.original;
			const groupKey = `system_group.${d.system_group}`;
			const groupName = tableSettings[groupKey];

			if (!d.system_group || !groupName) {
				return (
					<div className={style.playingOnCell}>
						<span className={style.playingOnEmpty}>—</span>
					</div>
				);
			}

			return (
				<div className={style.playingOnCell}>
					<div className={style.playingOnChip}>
						<span className={style.chipLabel}>{groupName}</span>
						<button
							type="button"
							className={style.chipX}
							aria-label={`Unassign ${groupName}`}
							data-test-id="buildingUnassignGroupButton"
							onClick={(e) => {
								e.stopPropagation();
								props.buildingsView.unassignGroup(d);
							}}
							style={{ background: 'transparent', border: 'none' }}
						>×</button>
					</div>
				</div>
			);
		},
		disableFilters: true,
	});

	const columns = React.useMemo(
		() => {
			return [
				{
					Header:  'BuildingsTable',
					columns: buildingColumns,
				},
			];
		},
		[ buildingColumns, ]
	);



	React.useEffect(() => {
		setFilteredRowsCount(data.length);
		const totalPages = Math.ceil(data.length / itemsPerPage) || 1;
		if (page > totalPages) {
			setPage(1);
		}
	}, [data.length, itemsPerPage, page]);

	let disabledUnlessChecked = array.some(obj => obj.isChecked)
		? 'button'
		: `button ${style.button} button-disabled`;

	let secondaryNavButtons = [
		{
			onClick: (e) => {
				e.preventDefault();
				const {
					buildingsView,
				} = props;

				const selectedBuildings = buildingsView.state.buildingsDataArray.filter((building) => {
					return building.isChecked;
				});

				if (selectedBuildings.length === 0) {
					return;
				}

				buildingsView.setState({
				    confirmModal:        true,
				    confirmModalContent: (
				        <>
				            <p>{`Delete ${selectedBuildings.length} building(s)?`}</p>
				            <div className="confirm-modal-buttons">
								<button className="button" data-test-id="buildingsBulkDeleteConfirmYesButton" onClick={(e) => {
								    e.preventDefault();
								    deleteBuildings(selectedBuildings, accountUUID)
										.then((res) => {
											if (res && res.error) {
												// Re-fetch after a failure too: the rows may be
												// gone (unknown outcome), or a 404 may name a
												// row deleted elsewhere that would otherwise
												// fail every retry.
												if (typeof app.setNetworkErrorModal === 'function') {
													app.setNetworkErrorModal(false, res.message);
												}
												app.fetchAccountBuildings();

												return;
											}

											app.fetchAccountBuildings();
										});
								    buildingsView.setState({
								        confirmModal:        false,
								        confirmModalContent: '',
								    });
								}}>Yes</button>
								<button className="button" onClick={(e) => {
								    e.preventDefault();
								    buildingsView.setState({
								        confirmModal:        false,
								        confirmModalContent: '',
				                    });
								}}>No</button>
				            </div>
				        </>
				    ),
				});
			},
			className: disabledUnlessChecked,
			icon:      'trash',
			text:      'Delete',
			testId:    'buildingsDeleteButton',
		},
		{
			onClick: (e) => {
				e.preventDefault();
				app.openModal({
					modalSize:  2,
					showModal:  true,
					modalProps: {
						title:          'New Building',
						pendingChanges: false,
						// Building writes persist immediately; the header X must not empty
						// the page-wide bag and discard edits staged elsewhere.
						keepPendingChanges: true,
						jsx:            (
							<BuildingsModal
								app={app}
								accountUUID={accountUUID}
								systemGroupUUID={systemGroupUUID}
								tableSettings={tableSettings}
								closeModal={closeModal}
							/>
						),
					},
				});
			},
			className: 'button',
			icon:      'plus',
			text:      'Add New',
			testId:    'buildingsAddNewButton',
		},
		{
			className: 'button' + (previousPageAvailable ? '' : ' button-disabled'),
			onClick:   () => {
				if (previousPageAvailable) {
					setPage(page - 1);
				}
			},
			text: '< Previous',
		},
		{
			className: 'button' + (nextPageAvailable ? '' : ' button-disabled'),
			onClick:   () => {
				if (nextPageAvailable) {
					setPage(page + 1);
				}
			},
			text: 'Next >',
		},
	];

	const tertiaryNavButtons = [
		{
			className: 'button' + (previousPageAvailable ? '' : ' button-disabled'),
			onClick:   () => {
				if (previousPageAvailable) {
					setPage(page - 1);
				}
			},
			text: '< Previous',
		},
		{
			className: 'button' + (nextPageAvailable ? '' : ' button-disabled'),
			onClick:   () => {
				if (nextPageAvailable) {
					setPage(page + 1);
				}
			},
			text: 'Next >',
		},
	];

	const resizingDataColsRef = React.useRef(false);
	const resizingDataColIdRef = React.useRef('');
	const startDataColXRef = React.useRef(0);
	const startDataColWidthRef = React.useRef(0);
	const startTotalWidthRef = React.useRef(0);
	const startOtherColsWidthRef = React.useRef(0);
	const startFlexesRef = React.useRef({});
	const visibleDataColsRef = React.useRef([]);
	const dragRafRef = React.useRef(null);
	const liveFlexesRef = React.useRef({});
	const wrapperRef = React.useRef(null);
	const draggingColNodesRef = React.useRef({});

	const [dataColumnFlexPx, setDataColumnFlexPx] = React.useState(() => {
		try {
			const saved = localStorage.getItem(colFlexStorageKey);
			const parsed = saved ? JSON.parse(saved) : null;
			return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
		} catch (e) {
			return {};
		}
	});

	React.useEffect(() => {
		try {
			const saved = localStorage.getItem(colFlexStorageKey);
			const parsed = saved ? JSON.parse(saved) : null;
			setDataColumnFlexPx(parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {});
		} catch (e) {
			setDataColumnFlexPx({});
		}
	}, [colFlexStorageKey]);

	React.useEffect(() => {
		try {
			localStorage.setItem(colFlexStorageKey, JSON.stringify(dataColumnFlexPx));
		} catch (e) {}
	}, [dataColumnFlexPx, colFlexStorageKey]);

	const onDataColHandleMouseMove = React.useCallback((e) => {
		if (!resizingDataColsRef.current || !resizingDataColIdRef.current) return;
		if (dragRafRef.current !== null) return;
		
		const clientX = e.clientX;
		dragRafRef.current = requestAnimationFrame(() => {
			dragRafRef.current = null;
			
			const dx = clientX - startDataColXRef.current;
			const startWidth = startDataColWidthRef.current;
			const startTotal = startTotalWidthRef.current;
			const startOther = startOtherColsWidthRef.current;
			
			const minOtherColsWidth = (visibleDataColsRef.current.length - 1) * 50;
			
			let newWidth = startWidth + dx;
			newWidth = Math.max(50, newWidth);
			newWidth = Math.min(newWidth, startTotal - minOtherColsWidth);
			
			const otherScale = startOther > 0 ? (startTotal - newWidth) / startOther : 1;
			
			const newFlexes = {};
			visibleDataColsRef.current.forEach(col => {
				if (col.id === resizingDataColIdRef.current) {
					newFlexes[col.id] = newWidth;
				} else {
					newFlexes[col.id] = Math.max(50, startFlexesRef.current[col.id] * otherScale);
				}
			});
			
			liveFlexesRef.current = newFlexes;
			
			if (wrapperRef.current) {
				Object.keys(newFlexes).forEach(id => {
					const w = newFlexes[id];
					const px = `${Math.round(w)}px`;
					const nodes = draggingColNodesRef.current[id] || [];
					nodes.forEach(el => {
						el.style.flex = `0 0 ${px}`;
						el.style.width = px;
						el.style.minWidth = px;
						el.style.maxWidth = px;
					});
				});
			}
		});
	}, []);

	const onDataColHandleMouseUp = React.useCallback(() => {
		resizingDataColsRef.current = false;
		resizingDataColIdRef.current = '';
		if (dragRafRef.current !== null) {
			cancelAnimationFrame(dragRafRef.current);
			dragRafRef.current = null;
		}
		const finalFlexes = liveFlexesRef.current;
		if (Object.keys(finalFlexes).length > 0) {
			setDataColumnFlexPx(prev => ({ ...prev, ...finalFlexes }));
			liveFlexesRef.current = {};
		}
		draggingColNodesRef.current = {};
		document.removeEventListener('mousemove', onDataColHandleMouseMove);
		document.removeEventListener('mouseup', onDataColHandleMouseUp);
	}, [onDataColHandleMouseMove]);

	const onDataColHandleMouseDown = React.useCallback((e, columnId) => {
		e.preventDefault();
		e.stopPropagation();
		
		const visibleDataCols = buildingColumns.filter(c => c.id !== 'checkbox' && !hiddenColumns.includes(c.id));
		const colIndex = visibleDataCols.findIndex(c => c.id === columnId);
		if (colIndex === -1) return;
		
		resizingDataColsRef.current = true;
		resizingDataColIdRef.current = columnId;
		startDataColXRef.current = e.clientX;
		visibleDataColsRef.current = visibleDataCols;
		
		const nodesToCache = {};
		if (wrapperRef.current) {
			visibleDataCols.forEach(col => {
				nodesToCache[col.id] = Array.from(wrapperRef.current.querySelectorAll(`[data-col-id="${col.id}"]`));
			});
		}
		draggingColNodesRef.current = nodesToCache;
		
		const headerCell = e.currentTarget ? e.currentTarget.parentElement : null;
		const headerRow = headerCell ? headerCell.parentElement : null;
		
		const currentFlexes = {};
		let totalWidth = 0;
		let colWidth = 150;
		
		if (headerRow) {
			const cells = Array.from(headerRow.children);
			const allVisibleCols = buildingColumns.filter(c => !hiddenColumns.includes(c.id));
			
			allVisibleCols.forEach((col, idx) => {
				if (col.id === 'checkbox') return;
				const cell = cells[idx];
				const w = cell ? Math.round(cell.getBoundingClientRect().width) : (dataColumnFlexPx[col.id] || 150);
				currentFlexes[col.id] = w;
				totalWidth += w;
				if (col.id === columnId) colWidth = w;
			});
		} else {
			visibleDataCols.forEach(col => {
				const w = dataColumnFlexPx[col.id] || 150;
				currentFlexes[col.id] = w;
				totalWidth += w;
				if (col.id === columnId) colWidth = w;
			});
		}
		
		startFlexesRef.current = currentFlexes;
		startDataColWidthRef.current = colWidth;
		startTotalWidthRef.current = totalWidth;
		startOtherColsWidthRef.current = totalWidth - colWidth;
		
		setDataColumnFlexPx(prev => ({ ...prev, ...currentFlexes }));

		document.addEventListener('mousemove', onDataColHandleMouseMove);
		document.addEventListener('mouseup', onDataColHandleMouseUp);
	}, [buildingColumns, hiddenColumns, dataColumnFlexPx, onDataColHandleMouseMove, onDataColHandleMouseUp]);

	React.useEffect(() => {
		return () => {
			if (dragRafRef.current !== null) {
				cancelAnimationFrame(dragRafRef.current);
			}
			document.removeEventListener('mousemove', onDataColHandleMouseMove);
			document.removeEventListener('mouseup', onDataColHandleMouseUp);
		};
	}, [onDataColHandleMouseMove, onDataColHandleMouseUp]);


	const getColumnStyle = (columnId) => {
		if (columnId === 'checkbox') {
			return { flex: '0 0 50px', width: '50px', minWidth: '50px', maxWidth: '50px', left: '0px', position: 'sticky', zIndex: 5 };
		}
		const w = dataColumnFlexPx[columnId];
		if (typeof w === 'number' && w > 0) {
			const px = `${Math.round(w)}px`;
			return { flex: `0 0 ${px}`, width: px, minWidth: px, maxWidth: px };
		}
		return { flex: '1 1 0%', minWidth: '50px' };
	};

	const headerStyles = (column) => getColumnStyle(column.id);
	const bodyStyles = (cell) => getColumnStyle(cell.column.id);

	const checkboxClick = (cell) => {
		const {
			buildingsView,
		} = props;

		const targetM1Key = cell.row.original.m1Key;
		const buildingsDataArray = array.map(building =>
			building.m1Key === targetM1Key
				? { ...building, isChecked: !building.isChecked }
				: building
		);

		buildingsView.setState({
			buildingsDataArray,
		});
	};

	const cellOnClick = (cell, row) => {
		return (e) => {
			e.preventDefault();
			if (cell.column.id === 'checkbox') {
				checkboxClick(cell);
			} else {
				app.openModal({
					modalSize:  2,
					showModal:  true,
					modalProps: {
						title:          'Edit Building',
						pendingChanges: false,
						keepPendingChanges: true,
						jsx:            (
							<BuildingsModal
								app={app}
								buildingName={cell.row.original.vals.buildingName}
								displayName={cell.row.original.vals.displayName}
								buildingM1={cell.row.original.m1Key}
								originalSystemGroupUUID={row.original.system_group}
								tableSettings={tableSettings}
								isEdit={true}
								closeModal={closeModal}
								buildingsView={props.buildingsView}
							/>
						),
					},
				});
			}
		};
	};

	return (
		<div ref={wrapperRef} style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
			<div className='secondaryNav topNav'>
				{secondaryNavButtons.map((button, i) => {
					return (
						<div
							key={`button_${i}`}
							className={button.className}
							data-test-id={button.testId}
							onClick={button.onClick}
						>
							{button.icon ? <CustomIcon icon={button.icon} /> : ''}{button.text}
						</div>
					);
				})}
			</div>

			<div className={`secondaryNav ${style.slideshowToolbar}`}>
				<div className={style.slideshowSearchContainer}>
					<div className={style.slideshowTopSearch} onClick={(e) => e.stopPropagation()}>
						<CustomIcon icon="search" />
						<input
							type="search"
							value={globalSearchQuery}
							onChange={(e) => setGlobalSearchQuery(e.target.value)}
							placeholder={`Search ${array.length} buildings`}
							aria-label="Search buildings"
							onClick={(e) => e.stopPropagation()}
						/>
						{globalSearchQuery ? (
							<button
								type="button"
								className={'button'}
								onClick={(e) => {
									e.preventDefault();
									setGlobalSearchQuery('');
								}}
							>
								Clear
							</button>
						) : null}
					</div>
				</div>

				<div className={style.slideshowToolbarRight}>
					<button
						ref={columnMenuButtonRef}
						type="button"
						className={'button'}
						onClick={() => setShowColumnMenu(!showColumnMenu)}
					>
						<CustomIcon icon={'columns'} /> Show/Hide
					</button>
				</div>
			</div>

			{showColumnMenu && (
				<div
					ref={columnMenuRef}
					className={style.slideshowColumnMenu}
					style={{ position: 'absolute', right: '20px', zIndex: 2500 }}
					onClick={(e) => e.stopPropagation()}
				>
					<div className={style.slideshowColumnMenuTitle}>Columns</div>
					{buildingColumns.map((col) => {
						if (col.id === 'checkbox') return null;
						const isVisible = !hiddenColumns.includes(col.id);
						const visibleCount = buildingColumns.filter(c => c.id !== 'checkbox' && !hiddenColumns.includes(c.id)).length;
						const disableHide = isVisible && visibleCount <= 1;
						return (
							<label key={col.id} className={style.slideshowColumnMenuItem}>
								<input
									type="checkbox"
									checked={isVisible}
									disabled={disableHide}
									onChange={() => {
										if (isVisible) {
											setHiddenColumns([...hiddenColumns, col.id]);
										} else {
											setHiddenColumns(hiddenColumns.filter(id => id !== col.id));
										}
									}}
								/>
								<span>{col.Header}</span>
							</label>
						);
					})}
				</div>
			)}

			<VultureTable
				style={style}
				columns={columns}
				data={data}
				fd={fd}
				columnsToHide={hiddenColumns}
				secondaryNavButtons={[]}
				showSecondaryNav={false}
				hideFilterRow={true}
				hideHorizontalScrollbar={true}
				hideTopScroller={true}
				tertiaryNavButtons={tertiaryNavButtons}
				cellOnClick={cellOnClick}
				theadColStyles={headerStyles}
				filterRowColStyles={headerStyles}
				tbodyColStyles={bodyStyles}
				allRowsSelected={allRowsSelected}
				toggleSelectAllRows={toggleSelectAllRows}
				page={page}
				itemsPerPage={itemsPerPage}
				onItemsPerPageChange={(newItemsPerPage) => {
					setItemsPerPage(newItemsPerPage);
					setPage(1);
				}}
				onFilter={onFilter}
				totalResults={filteredRowsCount}
				onColumnResizeMouseDown={onDataColHandleMouseDown}
				isColumnResizable={(col) => !col.columns && col.id !== 'checkbox'}
			/>
		</div>
	);
};

export default BuildingsTable;